import type { User as FirebaseUser } from 'firebase/auth';

export type { FirebaseUser };

/** Status of a CMS user account. */
export type UserStatus = 'pending' | 'approved' | 'rejected' | 'deactivated';

/** Top-level role — determines base access. Permissions further scope what editors/viewers can do. */
export type UserRole = 'admin' | 'editor' | 'viewer';

/** The three content audiences. */
export type Audience = 'mothers' | 'fathers' | 'grandparents';

export const AUDIENCES: Audience[] = ['mothers', 'fathers', 'grandparents'];

export const AUDIENCE_LABELS: Record<Audience, string> = {
  mothers:     'Mothers',
  fathers:     'Fathers',
  grandparents:'Grandparents',
};

/** Colour classes for each audience (Tailwind). */
export const AUDIENCE_COLORS: Record<Audience, string> = {
  mothers:     'text-pink-600 bg-pink-50 border-pink-200',
  fathers:     'text-blue-600 bg-blue-50 border-blue-200',
  grandparents:'text-purple-600 bg-purple-50 border-purple-200',
};

/** Per-audience content permissions. */
export interface AudiencePermissions {
  view:           boolean;
  add:            boolean;
  edit:           boolean;
  delete:         boolean;
  uploadFiles:    boolean;
  removeFiles:    boolean;
  copyContent:    boolean;
  approveContent: boolean;
  publishContent: boolean;
}

/** Global CMS permissions not tied to a specific audience. */
export interface GlobalPermissions {
  viewDashboard: boolean;
  manageUsers:   boolean;
  viewAuditLogs: boolean;
}

export interface UserPermissions {
  mothers:     AudiencePermissions;
  fathers:     AudiencePermissions;
  grandparents:AudiencePermissions;
  global:      GlobalPermissions;
}

/** Default empty permission set for new users. */
export const DEFAULT_AUDIENCE_PERMISSIONS: AudiencePermissions = {
  view:           false,
  add:            false,
  edit:           false,
  delete:         false,
  uploadFiles:    false,
  removeFiles:    false,
  copyContent:    false,
  approveContent: false,
  publishContent: false,
};

export const DEFAULT_USER_PERMISSIONS: UserPermissions = {
  mothers:     { ...DEFAULT_AUDIENCE_PERMISSIONS },
  fathers:     { ...DEFAULT_AUDIENCE_PERMISSIONS },
  grandparents:{ ...DEFAULT_AUDIENCE_PERMISSIONS },
  global: {
    viewDashboard: false,
    manageUsers:   false,
    viewAuditLogs: false,
  },
};

/** Firestore user document shape. */
export interface CmsUser {
  uid:           string;
  email:         string;
  displayName:   string;
  photoURL:      string | null;
  status:        UserStatus;
  role:          UserRole;
  permissions:   UserPermissions;
  createdAt:     Date | null;
  approvedAt:    Date | null;
  approvedBy:    string | null;
  lastLoginAt:   Date | null;
  deactivatedAt: Date | null;
  deactivatedBy: string | null;
}

/** Lightweight user reference stored inside content records and audit logs. */
export interface UserRef {
  uid:         string;
  displayName: string;
  email:       string;
}
